import React, { useState } from 'react';
import { ProductTypeEnum } from '@koob/enums';
import useTripStore from '../../store/useTripStore';
import TripDayItem from '../TripDayItem';
import TripDayHotelCardBody from './TripDayHotelCardBody';
import TripDayHotelCardModals from './TripDayHotelCardModals';
import { useTripDayHotelRoomEditing } from './useTripDayHotelRoomEditing';
import { getTripDayHotelCardPresentation } from '../../logic/getTripDayHotelCardPresentation';
import type { TripDayHotelProps } from './TripDayHotel';

// The "loaded, distributed hotel" render of `TripDayHotel`, split into its
// own file (there is no US-060 precedent for this exact split — the TS
// props/slots this move adds pushed the orchestrator itself over the
// max-lines-per-function cap) so `TripDayHotel.tsx` stays the thin
// empty/unavailable/card dispatcher. Splits further into
// TripDayHotelCardBody (the visuals) and TripDayHotelCardModals (the three
// modals a loaded hotel day can open), for the same reason.
export default function TripDayHotelCard({
  dayIndex,
  date,
  addHotel,
  selectedType,
  data,
  selection,
  removeHotel,
  readOnly = false,
  canMutateHotel = true,
  canRemoveHotel = true,
  t,
  locale,
  tripHotelCardProps,
  renderAccessAlert,
  renderRoomSelectionModal,
}: {
  dayIndex: number;
  date: any;
  addHotel: (hotel: any) => void;
  selectedType: any;
  data: any;
  selection: any;
  removeHotel: () => void;
  readOnly?: boolean;
  canMutateHotel?: boolean;
  canRemoveHotel?: boolean;
  t: (key: string, options?: Record<string, any>) => string;
  locale?: string;
  tripHotelCardProps?: any;
  renderAccessAlert?: (info: { tooltip: string }) => React.ReactNode;
  renderRoomSelectionModal: TripDayHotelProps['renderRoomSelectionModal'];
}) {
  const updateHotelRoomSelection = useTripStore(
    (state) => state.updateHotelRoomSelection,
  );

  // Everything else Body/Modals need is either already shaped exactly as
  // `data`/`selection` carry it (spread below) or computed here.
  const {
    hotel,
    tripFilters,
    bookedRooms,
    isIncludedHotel,
    isGroupLoading,
    hasGroupError,
  } = data;
  const { isRoomUnavailable } = selection;

  const [showModal, setShowModal] = useState(false);
  const [showRoomSelectModal, setShowRoomSelectModal] = useState(false);
  const [showRoomsModal, setShowRoomsModal] = useState(false);

  const {
    canEditRoomSelection,
    canEditRoomComposition,
    handleSaveRoomsComposition,
  } = useTripDayHotelRoomEditing({
    dayIndex,
    hotel,
    tripFilters,
    bookedRooms,
    canMutateHotel,
    readOnly,
  });

  const { hasFloatingControls, showAccessAlert, accessAlertTooltip } =
    getTripDayHotelCardPresentation({
      canRemoveHotel,
      isIncludedHotel,
      isGroupLoading,
      hasGroupError,
      isRoomUnavailable,
      readOnly,
      isHotelSelected: selectedType === ProductTypeEnum.HOTEL,
      isMinStayUnmet: data.isMinStayUnmet,
      hotel,
      t,
    });

  return (
    <TripDayItem>
      {/* KD-3581 AC08 — the card is no longer one big link: only the icon
          beside the name navigates, so clicking the card (or the name) does
          nothing. */}
      <TripDayHotelCardBody
        dayIndex={dayIndex}
        {...data}
        {...selection}
        selectedType={selectedType}
        hasFloatingControls={hasFloatingControls}
        showAccessAlert={showAccessAlert}
        accessAlertTooltip={accessAlertTooltip}
        renderAccessAlert={renderAccessAlert}
        addHotel={addHotel}
        removeHotel={removeHotel}
        readOnly={readOnly}
        canRemoveHotel={canRemoveHotel}
        canEditRoomSelection={canEditRoomSelection}
        canEditRoomComposition={canEditRoomComposition}
        onShowSuggestedHotels={() => setShowModal(true)}
        onEditRoomSelection={() => setShowRoomSelectModal(true)}
        onEditRooms={() => setShowRoomsModal(true)}
        t={t}
      />

      <TripDayHotelCardModals
        dayIndex={dayIndex}
        date={date}
        {...data}
        locale={locale}
        tripHotelCardProps={tripHotelCardProps}
        showRoomsModal={showRoomsModal}
        setShowRoomsModal={setShowRoomsModal}
        showModal={showModal}
        setShowModal={setShowModal}
        showRoomSelectModal={showRoomSelectModal}
        setShowRoomSelectModal={setShowRoomSelectModal}
        onSaveRoomsComposition={handleSaveRoomsComposition}
        updateHotelRoomSelection={updateHotelRoomSelection}
        renderRoomSelectionModal={renderRoomSelectionModal}
        t={t}
      />
    </TripDayItem>
  );
}
